import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useAuthStore } from "../stores/authStore";
import LoginPage from "../pages/auth/LoginPage";
import RegisterPage from "../pages/auth/RegisterPage";
import CompaniesPage from "../pages/companies/CompaniesPage";
import DashboardLayout from "../layouts/DashboardLayout";
import DashboardPage from "../pages/dashboard/DashboardPage";
import UsersPage from "../pages/users/UsersPage";
import UserFormPage from "../pages/users/UserFormPage";
import CardHoldersPage from "../pages/cardHolders/CardHoldersPage";
import CardHolderFormPage from "../pages/cardHolders/CardHolderFormPage";
import HolderProfilePage from "../pages/cardHolders/HolderProfilePage";
import NFCCardsPage from "../pages/nfcCards/NFCCardsPage";
import SimpleRechargePage from "../pages/recharge/SimpleRechargePage";
import TransactionsPage from "../pages/transactions/TransactionsPage";
import TransactionDetailsPage from "../pages/transactions/TransactionDetailsPage";
import ProfilePage from "../pages/profile/ProfilePage";
import SalesPointsPage from "../pages/sales-points/SalesPointsPage";
import SalesPointFormPage from "../pages/sales-points/SalesPointFormPage";
import BranchesPage from "../pages/branches/BranchesPage";
import BranchFormPage from "../pages/branches/BranchFormPage";
import CategoriesPage from "../pages/categories/CategoriesPage";
import ProductsPage from "../pages/products/ProductsPage";
import ProductListPage from "../pages/products/ProductListPage";
import ProductImportPage from "../pages/products/ProductImportPage";
import ProductExcelEntryPage from "../pages/products/ProductExcelEntryPage";
import MyReportsPage from "../pages/reports/MyReportsPage";
import InventoryPage from "../pages/inventory/InventoryPage";
import StockTransfersPage from "../pages/inventory/StockTransfersPage";
import WarehousesPage from "../pages/warehouses/WarehousesPage";
import RetailPosPage from "../pages/sales/RetailPosPage";
import TablesPage from "../pages/tables/TablesPage";
import CheckBalancePage from "../pages/balance/CheckBalancePage";
import PurchasesPage from "../pages/purchases/PurchasesPage";
import SuppliersPage from "../pages/suppliers/SuppliersPage";
import CustomersPage from "../pages/customers/CustomersPage";
import RecipesPage from "../pages/recipes/RecipesPage";
import RechargesPage from "../pages/recharges/RechargesPage";
import ReportsPage from "../pages/reports/ReportsPage";
import SalesReportPage from "../pages/reports/SalesReportPage";
import GstBooksPage from "../pages/reports/GstBooksPage";
import ChartOfAccountsPage from "../pages/reports/ChartOfAccountsPage";
import JournalsPage from "../pages/reports/JournalsPage";
import InventoryAlertsPage from "../pages/reports/InventoryAlertsPage";
import SettingsPage from "../pages/settings/SettingsPage";
import VouchersPage from "../pages/vouchers/VouchersPage";
import AuditLogsPage from "../pages/auditLogs/AuditLogsPage";
import UserProfilePage from "../pages/users/UserProfilePage";
import RolesPage from "../pages/roles/RolesPage";
import RBACDemoPage from "../pages/demo/RBACDemoPage";
import ProtectedRoute from "../components/ProtectedRoute";
import LandingPage from "../pages/landing/LandingPage";
import PrivacyPolicyPage from "../pages/legal/PrivacyPolicyPage";
import ShortcutsPage from "../pages/shortcuts/ShortcutsPage";

const AppRoutes: React.FC = () => {
  const { isAuthenticated } = useAuthStore();

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
      <Route
        path="/login"
        element={
          isAuthenticated ? <Navigate to="/dashboard" replace /> : <LoginPage />
        }
      />
      <Route
        path="/register"
        element={
          isAuthenticated ? (
            <Navigate to="/dashboard" replace />
          ) : (
            <RegisterPage />
          )
        }
      />

      {/* Protected app shell (pathless layout — children keep absolute paths) */}
      <Route
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route path="dashboard" element={<DashboardPage />} />

        {/* Users Routes */}
        <Route
          path="users"
          element={
            <ProtectedRoute module="users">
              <UsersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="users/new"
          element={
            <ProtectedRoute module="users" requiredAction="canCreate">
              <UserFormPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="users/:id/edit"
          element={
            <ProtectedRoute module="users" requiredAction="canUpdate">
              <UserFormPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="users/:id"
          element={
            <ProtectedRoute module="users">
              <UserProfilePage />
            </ProtectedRoute>
          }
        />

        {/* Card Holders Routes */}
        <Route
          path="cardholders"
          element={
            <ProtectedRoute module="cardholders">
              <CardHoldersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="cardholders/new"
          element={
            <ProtectedRoute module="cardholders" requiredAction="canCreate">
              <CardHolderFormPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="cardholders/:id/edit"
          element={
            <ProtectedRoute module="cardholders" requiredAction="canUpdate">
              <CardHolderFormPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="cardholders/:id"
          element={
            <ProtectedRoute module="cardholders">
              <HolderProfilePage />
            </ProtectedRoute>
          }
        />

        <Route
          path="nfc-cards"
          element={
            <ProtectedRoute module="nfc_cards">
              <NFCCardsPage />
            </ProtectedRoute>
          }
        />

        {/* Quick Recharge Route */}
        <Route
          path="quick-recharge"
          element={
            <ProtectedRoute module="recharges" requiredAction="canCreate">
              <SimpleRechargePage />
            </ProtectedRoute>
          }
        />

        <Route
          path="check-balance"
          element={
            <ProtectedRoute module="nfc_cards">
              <CheckBalancePage />
            </ProtectedRoute>
          }
        />

        {/* Transactions Routes */}
        <Route
          path="transactions"
          element={
            <ProtectedRoute module="transactions">
              <TransactionsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="transactions/:id"
          element={
            <ProtectedRoute module="transactions">
              <TransactionDetailsPage />
            </ProtectedRoute>
          }
        />

        {/* Sales Points Routes */}
        <Route
          path="sales-points"
          element={
            <ProtectedRoute module="sales_points">
              <SalesPointsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="sales-points/new"
          element={
            <ProtectedRoute module="sales_points" requiredAction="canCreate">
              <SalesPointFormPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="sales-points/:id/edit"
          element={
            <ProtectedRoute module="sales_points" requiredAction="canUpdate">
              <SalesPointFormPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="sales-points/:id"
          element={
            <ProtectedRoute module="sales_points">
              <SalesPointFormPage />
            </ProtectedRoute>
          }
        />

        {/* Branches Routes */}
        <Route
          path="branches"
          element={
            <ProtectedRoute module="branches">
              <BranchesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="branches/new"
          element={
            <ProtectedRoute module="branches" requiredAction="canCreate">
              <BranchFormPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="branches/:id/edit"
          element={
            <ProtectedRoute module="branches" requiredAction="canUpdate">
              <BranchFormPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="branches/:id"
          element={
            <ProtectedRoute module="branches">
              <BranchFormPage />
            </ProtectedRoute>
          }
        />

        {/* Categories Routes */}
        <Route
          path="categories"
          element={
            <ProtectedRoute module="categories">
              <CategoriesPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="product-master"
          element={
            <ProtectedRoute module="products">
              <ProductsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="products"
          element={<Navigate to="/product-master" replace />}
        />
        <Route
          path="product-list"
          element={
            <ProtectedRoute module="product_sales_points">
              <ProductListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="product-import"
          element={
            <ProtectedRoute module="product_sales_points">
              <ProductImportPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="product-excel-entry"
          element={
            <ProtectedRoute module="product_sales_points">
              <ProductExcelEntryPage />
            </ProtectedRoute>
          }
        />

        {/* Inventory Routes */}
        <Route
          path="warehouses"
          element={
            <ProtectedRoute module="warehouses">
              <WarehousesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="inventory"
          element={
            <ProtectedRoute module="inventory">
              <InventoryPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="stock-transfers"
          element={
            <ProtectedRoute module="stock_transfers">
              <StockTransfersPage />
            </ProtectedRoute>
          }
        />

        {/* Sales Routes — Retail POS + Restaurant KOT */}
        <Route
          path="sales"
          element={
            <ProtectedRoute module="sales">
              <RetailPosPage />
            </ProtectedRoute>
          }
        />
        <Route path="sales/restaurant" element={<Navigate to="/reports/sales" replace />} />
        <Route path="sales/simple" element={<Navigate to="/sales" replace />} />
        <Route path="sales/petpooja" element={<Navigate to="/reports/sales" replace />} />

        <Route
          path="tables"
          element={
            <ProtectedRoute module="tables">
              <TablesPage />
            </ProtectedRoute>
          }
        />

        {/* Purchases Routes */}
        <Route
          path="purchases"
          element={
            <ProtectedRoute module="purchases">
              <PurchasesPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="suppliers"
          element={
            <ProtectedRoute module="suppliers">
              <SuppliersPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="customers"
          element={
            <ProtectedRoute module="customers">
              <CustomersPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="vouchers"
          element={
            <ProtectedRoute module="vouchers">
              <VouchersPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="recipes"
          element={
            <ProtectedRoute module="recipes">
              <RecipesPage />
            </ProtectedRoute>
          }
        />

        {/* Recharges Routes */}
        <Route
          path="recharges"
          element={
            <ProtectedRoute module="recharges">
              <RechargesPage />
            </ProtectedRoute>
          }
        />

        {/* Reports Routes */}
        <Route
          path="my-reports"
          element={
            <ProtectedRoute module="reports">
              <MyReportsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="reports"
          element={
            <ProtectedRoute module="reports">
              <ReportsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="reports/sales"
          element={
            <ProtectedRoute module="reports">
              <SalesReportPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="reports/gst-books"
          element={
            <ProtectedRoute module="reports">
              <GstBooksPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="reports/chart-of-accounts"
          element={
            <ProtectedRoute module="reports">
              <ChartOfAccountsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="reports/journals"
          element={
            <ProtectedRoute module="reports">
              <JournalsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="reports/inventory-alerts"
          element={
            <ProtectedRoute module="reports">
              <InventoryAlertsPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="companies"
          element={
            <ProtectedRoute requireRoles={["PLATFORM_ADMIN"]}>
              <CompaniesPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="roles"
          element={
            <ProtectedRoute
              module="permissions"
              requiredAction="canRead"
              requireRoles={["SUPER_ADMIN", "PLATFORM_ADMIN"]}
            >
              <RolesPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="settings"
          element={
            <ProtectedRoute module="settings" requiredAction="canRead">
              <SettingsPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="audit-logs"
          element={
            <ProtectedRoute module="audit_logs" requiredAction="canRead">
              <AuditLogsPage />
            </ProtectedRoute>
          }
        />

        {/* Shortcuts Guide */}
        <Route path="shortcuts" element={<ShortcutsPage />} />

        {/* Profile */}
        <Route path="profile" element={<ProfilePage />} />

        {/* RBAC Demo */}
        <Route
          path="rbac-demo"
          element={
            <ProtectedRoute module="dashboard">
              <RBACDemoPage />
            </ProtectedRoute>
          }
        />
      </Route>

      {/* Catch all — public home or dashboard */}
      <Route
        path="*"
        element={
          <Navigate to={isAuthenticated ? "/dashboard" : "/"} replace />
        }
      />
    </Routes>
  );
};

export default AppRoutes;
